// ─── Sprint 093 — Operator Avatar ────────────────────────────────────────────
// Circular profile picture shown in the Shell header and Learn to Roam page.
// Falls back to operator initials if no photo is uploaded.
//
// Avatar source priority (highest → lowest):
//   1. authUser.avatarUrl  — server-authoritative, fresh signed R2 URL via GET /auth/me
//   2. persona.avatarDataUrl — localStorage fallback (base64 from pre-R2 uploads or
//      optimistic update during the brief window before the server response arrives)
// After page refresh, React Query re-fetches GET /auth/me and generates a fresh
// signed URL, so source (1) is always current after the initial load completes.

import { useState, useEffect } from 'react';
import { loadPersona, getInitials } from '@/services/OperatorPersonaService';
import { getPendingCount } from '@/services/RoamerBackpackService';
import { useAuthUser } from '@/hooks/useRole';
import type { OperatorPersona } from '@/types/operatorPersona';

interface OperatorAvatarProps {
  /** 'sm' = 36px (mobile header), 'md' = 44px (desktop / page header) */
  size?: 'sm' | 'md';
  onClick?: () => void;
  /** Whether to show the backpack badge count. */
  showBadge?: boolean;
}

export function OperatorAvatar({ size = 'md', onClick, showBadge = true }: OperatorAvatarProps) {
  const [persona, setPersona] = useState<OperatorPersona | null>(null);
  const [badge, setBadge]     = useState(0);

  // Server-authoritative user — provides a fresh signed R2 URL on every page load.
  // This is the primary avatar source and persists across sessions/refreshes/logins.
  const authUser = useAuthUser();

  useEffect(() => {
    setPersona(loadPersona());
    if (showBadge) setBadge(getPendingCount());
  }, [showBadge]);

  // Refresh on backpack changes and persona updates (name / avatar edits)
  useEffect(() => {
    function refresh() {
      setPersona(loadPersona());
      if (showBadge) setBadge(getPendingCount());
    }
    window.addEventListener('nomad:backpack-updated', refresh);
    // nomad:data-changed fires after every updatePersona() / savePersona() call,
    // including profile saves from ProfilePage. This keeps the avatar in sync
    // immediately without requiring a full page reload.
    window.addEventListener('nomad:data-changed',    refresh);
    return () => {
      window.removeEventListener('nomad:backpack-updated', refresh);
      window.removeEventListener('nomad:data-changed',    refresh);
    };
  }, [showBadge]);

  const dim = size === 'sm' ? 36 : 44;
  const initials = persona?.displayName ? getInitials(persona.displayName) : 'OP';

  // Server URL wins (fresh signed R2 URL that never expires in the session).
  // Persona fallback covers the brief loading window after a fresh page load.
  const avatarSrc = authUser?.avatarUrl ?? persona?.avatarDataUrl ?? null;

  return (
    <button
      onClick={onClick}
      aria-label="Open Operator Center"
      style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
    >
      {/* Avatar circle */}
      <span
        style={{
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          width:           dim,
          height:          dim,
          borderRadius:    '50%',
          overflow:        'hidden',
          border:          '2px solid rgba(139,92,246,0.4)',
          background:      avatarSrc
            ? 'transparent'
            : 'linear-gradient(135deg, #1e3a8a 0%, #4c1d95 100%)',
          cursor:          'pointer',
          transition:      'box-shadow 0.2s ease, border-color 0.2s ease',
          flexShrink:      0,
        }}
        className="operator-avatar-circle"
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={persona?.displayName ?? authUser?.displayName ?? 'Avatar'}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <span style={{
            fontFamily:    'monospace',
            fontSize:      size === 'sm' ? 11 : 13,
            fontWeight:    700,
            color:         '#e2e8f0',
            letterSpacing: '0.05em',
            userSelect:    'none',
          }}>
            {initials}
          </span>
        )}
      </span>

      {/* Green online indicator */}
      <span style={{
        position:     'absolute',
        bottom:       1,
        right:        1,
        width:        size === 'sm' ? 8 : 10,
        height:       size === 'sm' ? 8 : 10,
        borderRadius: '50%',
        background:   '#22c55e',
        border:       '2px solid #0a0e1a',
        pointerEvents:'none',
      }} />

      {/* Backpack badge */}
      {showBadge && badge > 0 && (
        <span style={{
          position:      'absolute',
          top:           -4,
          right:         -4,
          minWidth:      16,
          height:        16,
          borderRadius:  8,
          background:    'linear-gradient(135deg, #8b5cf6, #3b82f6)',
          color:         '#fff',
          fontSize:      9,
          fontWeight:    700,
          fontFamily:    'monospace',
          display:       'flex',
          alignItems:    'center',
          justifyContent:'center',
          padding:       '0 3px',
          border:        '2px solid #0a0e1a',
          pointerEvents: 'none',
        }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}

      <style>{`
        .operator-avatar-circle:hover {
          box-shadow: 0 0 0 3px rgba(139,92,246,0.3), 0 0 16px rgba(59,130,246,0.4);
          border-color: rgba(139,92,246,0.8) !important;
        }
      `}</style>
    </button>
  );
}
